/**
 * HelpCenterPage — Central de Ajuda estilo Cakto (Fase 1 + ajuste de layout).
 *
 * Leitura: home com "Qual é a sua dúvida?" + busca grande + CARDS de categoria
 * -> lista da categoria -> ArticleView (cabeçalho + índice lateral automático).
 * A busca (ILIKE no backend) sobrepõe o agrupamento enquanto há termo.
 * Admin (super_admin OU pode_editar_faq): CRUD com editor Markdown + upload R2.
 * Deep-link: ?article=<id> abre o artigo direto (usado pelo widget em tela cheia).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HelpCircle, Search, Plus, Edit3, Trash2, X, ArrowLeft, ArrowRight,
  Loader2, ChevronRight, MessagesSquare,
} from 'lucide-react';
import { categoryIcon } from './helpCategoryIcons';

import Sidebar from '../Sidebar';
import Header from '../Header';
import PageHeader from '../ui/PageHeader';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAdmin } from '../../contexts/AdminContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import ArticleView from './ArticleView';
import MarkdownEditor from './MarkdownEditor';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_FORM = {
  titulo: '', resumo: '', categoria: 'Geral', video_url: '',
  thumbnail_url: '', conteudo_rich: '', publicado: true, ordem: 0,
};

export default function HelpCenterPage({ onLogout }) {
  const { t } = useTranslation();
  usePageTitle(t('help.title', 'Central de Ajuda'));
  const { sidebarWidth, isMobile } = useSidebar();
  const { podeEditarFaq, isSuperAdmin, isModerator } = useAdmin();
  const isHelpAdmin = isSuperAdmin || isModerator;

  // ── Visão ADMIN: conversas dos usuários com o Planner (acompanhamento) ──
  const [adminMode, setAdminMode] = useState(false);
  const [adminConvos, setAdminConvos] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSel, setAdminSel] = useState(null);     // conversa selecionada
  const [adminMsgs, setAdminMsgs] = useState(null);   // transcript
  const openAdminMode = async () => {
    setAdminMode(true); setAdminSel(null); setAdminMsgs(null); setAdminLoading(true);
    try {
      const r = await axios.get(`${API}/help/conversations`);
      setAdminConvos(r.data.conversations || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao carregar conversas'); }
    finally { setAdminLoading(false); }
  };
  const openAdminConvo = async (c) => {
    setAdminSel(c); setAdminMsgs(null);
    try {
      const r = await axios.get(`${API}/help/conversations/${c.id}/messages`);
      setAdminMsgs(r.data.messages || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao abrir conversa'); }
  };

  // Toggle GLOBAL do widget de atendimento (só super admin vê/muda).
  const [widgetEnabled, setWidgetEnabled] = useState(null);
  const [widgetToggling, setWidgetToggling] = useState(false);
  useEffect(() => {
    if (!isSuperAdmin) return;
    axios.get(`${API}/help/widget-config`)
      .then((r) => setWidgetEnabled(r.data?.enabled !== false))
      .catch(() => setWidgetEnabled(true));
  }, [isSuperAdmin]);
  const toggleWidget = async () => {
    if (widgetToggling || widgetEnabled === null) return;
    setWidgetToggling(true);
    try {
      const next = !widgetEnabled;
      await axios.put(`${API}/help/widget-config`, { enabled: next });
      setWidgetEnabled(next);
      toast.success(next ? 'Widget de atendimento ATIVADO para todos.' : 'Widget de atendimento OCULTADO para todos.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar o widget');
    } finally {
      setWidgetToggling(false);
    }
  };
  const [searchParams, setSearchParams] = useSearchParams();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [selected, setSelected] = useState(null);

  // Admin modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [saving, setSaving] = useState(false);

  const loadArticles = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('search', q);
      const res = await axios.get(`${API}/help/articles?${params.toString()}`);
      setArticles(res.data.articles || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar artigos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => loadArticles(search.trim()), search ? 300 : 0);
    return () => clearTimeout(id);
  }, [search, loadArticles]);

  // Deep-link: ?article=<id> abre o artigo (vindo do widget em tela cheia).
  // Dirigido por searchParams (NÃO mount-only): o widget é global e navega pra
  // /central-de-ajuda?article=ID mesmo já estando na página — aí só a query muda
  // (sem remontar), então o effect precisa reagir a searchParams. O finally
  // limpa o param -> re-execução sai cedo (sem fetch duplicado).
  useEffect(() => {
    const aid = searchParams.get('article');
    if (!aid) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/help/articles/${aid}`);
        setSelected(res.data);
      } catch {
        toast.error('Artigo não encontrado');
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('article');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams]);

  const categories = useMemo(() => {
    const map = new Map();
    for (const a of articles) {
      const cat = a.categoria || 'Geral';
      map.set(cat, (map.get(cat) || 0) + 1);
    }
    return Array.from(map.entries()).map(([cat, count]) => ({ cat, count }));
  }, [articles]);

  const categoryArticles = useMemo(
    () => (activeCategory ? articles.filter((a) => (a.categoria || 'Geral') === activeCategory) : []),
    [articles, activeCategory],
  );

  // ── Admin handlers ───────────────────────────────────────────────────────
  const openCreate = () => { setEditing(null); setForm({ ...INITIAL_FORM, categoria: activeCategory || 'Geral' }); setShowModal(true); };
  const openEdit = (a, e) => {
    if (e) e.stopPropagation();
    setEditing(a);
    setForm({
      titulo: a.titulo || '', resumo: a.resumo || '', categoria: a.categoria || 'Geral',
      video_url: a.video_url || '', thumbnail_url: a.thumbnail_url || '',
      conteudo_rich: a.conteudo_rich || '', publicado: a.publicado !== false, ordem: a.ordem || 0,
    });
    setShowModal(true);
  };
  const handleSave = async () => {
    if (!form.titulo.trim()) { toast.error('Título é obrigatório'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/help/articles/${editing.id}`, form);
        toast.success('Artigo atualizado!');
        if (selected && selected.id === editing.id) setSelected({ ...selected, ...form });
      } else {
        await axios.post(`${API}/help/articles`, form);
        toast.success('Artigo criado!');
      }
      setShowModal(false);
      loadArticles(search.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async (a, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Excluir o artigo "${a.titulo}"?`)) return;
    try {
      await axios.delete(`${API}/help/articles/${a.id}`);
      toast.success('Artigo excluído.');
      if (selected && selected.id === a.id) setSelected(null);
      loadArticles(search.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };
  const searching = !!search.trim();
  // Chave da "tela" atual — transição SEMPRE na mesma direção entre cards/categoria/busca.
  const screenKey = searching ? 'search' : activeCategory ? `cat-${activeCategory}` : 'cards';
  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '10px', boxSizing: 'border-box',
    border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-input)',
    color: 'var(--dp-text-primary)', fontSize: '13.5px', fontFamily: 'var(--dp-font)', outline: 'none',
  };
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' };

  const ArticleRow = ({ a }) => (
    <div onClick={() => setSelected(a)} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
      padding: '14px 16px', borderRadius: '12px', cursor: 'pointer',
      background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', transition: 'border-color 0.15s',
    }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.3)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14.5px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>{a.titulo}</span>
          {podeEditarFaq && a.publicado === false && (
            <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '6px', background: 'rgba(245,158,11,0.15)', color: '#F59E0B', fontWeight: 600 }}>rascunho</span>
          )}
        </div>
        {a.resumo && <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: 'var(--dp-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.resumo}</p>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
        {podeEditarFaq && (<>
          <button onClick={(e) => openEdit(a, e)} title="Editar" style={iconBtnSm}><Edit3 size={14} /></button>
          <button onClick={(e) => handleDelete(a, e)} title="Excluir" style={{ ...iconBtnSm, color: '#EF4444' }}><Trash2 size={14} /></button>
        </>)}
        <ChevronRight size={18} color="var(--dp-text-tertiary)" />
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`, marginTop: '60px', flex: 1, minWidth: 0,
        transition: 'margin-left 0.3s ease', animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        <PageHeader
          icon={<HelpCircle size={22} color="var(--dp-blue-500)" />}
          title={t('help.title', 'Central de Ajuda')}
          description={t('help.subtitle', 'Tire suas dúvidas sobre a DarkPlanner.')}
        >
          {isHelpAdmin && (
            <button
              onClick={() => (adminMode ? setAdminMode(false) : openAdminMode())}
              title="Acompanhar as conversas dos usuários com o Planner"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px',
                borderRadius: '10px', cursor: 'pointer', background: adminMode ? 'rgba(13,174,255,0.10)' : 'transparent',
                border: `1px solid ${adminMode ? 'rgba(13,174,255,0.35)' : 'var(--dp-border-medium, rgba(255,255,255,0.10))'}`,
                color: adminMode ? '#0DAEFF' : 'var(--dp-text-secondary)',
                fontSize: '12.5px', fontFamily: 'var(--dp-font)', fontWeight: 600,
              }}
            >
              <MessagesSquare size={14} /> Conversas
            </button>
          )}
          {isSuperAdmin && widgetEnabled !== null && (
            <button
              onClick={toggleWidget}
              disabled={widgetToggling}
              title="Mostra/oculta o balão de atendimento para TODOS os usuários"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px',
                borderRadius: '10px', cursor: widgetToggling ? 'default' : 'pointer',
                background: 'transparent', border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.10))',
                color: widgetEnabled ? '#34D399' : 'var(--dp-text-tertiary)',
                fontSize: '12.5px', fontFamily: 'var(--dp-font)', fontWeight: 600,
              }}
            >
              <span style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: widgetEnabled ? '#34D399' : 'var(--dp-text-tertiary)',
              }} />
              {widgetToggling ? 'Salvando...' : (widgetEnabled ? 'Widget: visível' : 'Widget: oculto')}
            </button>
          )}
          {podeEditarFaq && (
            <button className="dp-btn-cta dp-btn-cta--sm" onClick={openCreate}>
              <Plus size={14} /> {t('help.new_article', 'Novo artigo')} <ArrowRight size={14} />
            </button>
          )}
        </PageHeader>

        {/* Container ÚNICO centralizado — mesmo layout/alinhamento em home, categoria e artigo */}
        <div style={{ maxWidth: '1040px', margin: '0 auto', width: '100%' }}>
        {adminMode ? (
          /* ===== VISÃO ADMIN/MODERADOR: conversas dos usuários com o Planner ===== */
          <div style={{ maxWidth: '820px' }}>
            {adminSel ? (
              <div>
                <button onClick={() => { setAdminSel(null); setAdminMsgs(null); }} style={backBtn}>
                  <ArrowLeft size={16} /> Todas as conversas
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  {adminSel.ticket_number && (
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#0DAEFF', border: '1px solid rgba(13,174,255,0.35)', background: 'rgba(13,174,255,0.08)', borderRadius: '8px', padding: '2px 10px' }}>#{adminSel.ticket_number}</span>
                  )}
                  <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)' }}>{adminSel.titulo || 'Conversa'}</span>
                  {adminSel.user_email && <span style={{ fontSize: '12.5px', color: 'var(--dp-text-tertiary)' }}>{adminSel.user_email}</span>}
                  {adminSel.is_closed && <span style={{ fontSize: '11px', color: '#F59E0B' }}>Encerrada</span>}
                </div>
                {adminMsgs === null ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Loader2 size={24} className="dp-spin" color="var(--dp-blue-500)" /></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {adminMsgs.map((m, i) => (
                      <div key={i} style={{
                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '78%', padding: '10px 14px', borderRadius: '14px',
                        fontSize: '13.5px', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        background: m.role === 'user' ? 'rgba(13,174,255,0.12)' : 'var(--dp-bg-card)',
                        border: '1px solid var(--dp-border-subtle)', color: 'var(--dp-text-primary)',
                      }}>
                        <div style={{ fontSize: '10.5px', fontWeight: 700, color: 'var(--dp-text-tertiary)', marginBottom: '4px' }}>
                          {m.role === 'user' ? (adminSel.user_email || 'Usuário') : 'Planner'}
                          {m.created_at ? ` · ${new Date(m.created_at).toLocaleString('pt-BR')}` : ''}
                        </div>
                        {m.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '0 0 4px' }}>Conversas dos usuários</h2>
                <p style={{ fontSize: '12.5px', color: 'var(--dp-text-tertiary)', margin: '0 0 16px' }}>Acompanhe os atendimentos do Planner. Consulte pelo nº do ticket quando alguém chamar no suporte humano.</p>
                {adminLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Loader2 size={24} className="dp-spin" color="var(--dp-blue-500)" /></div>
                ) : adminConvos.length === 0 ? (
                  <p style={{ color: 'var(--dp-text-tertiary)', fontSize: '13px' }}>Nenhuma conversa ainda.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {adminConvos.map((c) => (
                      <div key={c.id} onClick={() => openAdminConvo(c)} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                        padding: '13px 16px', borderRadius: '12px', cursor: 'pointer',
                        background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', transition: 'border-color 0.15s',
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.3)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            {c.ticket_number && <span style={{ flexShrink: 0, fontSize: '12px', fontWeight: 700, color: '#0DAEFF' }}>#{c.ticket_number}</span>}
                            <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--dp-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.titulo || 'Conversa'}</span>
                            {c.is_closed && <span style={{ flexShrink: 0, fontSize: '10px', color: '#F59E0B' }}>Encerrada</span>}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.user_email ? `${c.user_email} · ` : ''}{c.last_message || ''}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', marginTop: 2 }}>
                            {c.updated_at ? new Date(c.updated_at).toLocaleString('pt-BR') : ''}
                          </div>
                        </div>
                        <ChevronRight size={18} color="var(--dp-text-tertiary)" style={{ flexShrink: 0 }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
        <>
          {/* ===== HERO — busca SEMPRE visível (todas as views, inclusive dentro do artigo) ===== */}
          <div style={{ margin: '8px 0 28px' }}>
            <h2 style={{ textAlign: 'center', color: 'var(--dp-text-primary)', fontSize: '26px', fontWeight: 700, letterSpacing: '-0.6px', margin: '0 0 18px' }}>
              {t('help.home_question', 'Qual é a sua dúvida?')}
            </h2>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--dp-text-tertiary)' }} />
              <input
                value={search}
                onChange={(e) => { const v = e.target.value; setSearch(v); if (v.trim()) { setActiveCategory(null); setSelected(null); } }}
                placeholder={t('help.search_placeholder', 'Buscar respostas...')}
                style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px 14px 46px', borderRadius: '14px', border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-card)', color: 'var(--dp-text-primary)', fontSize: '15px', fontFamily: 'var(--dp-font)', outline: 'none', transition: 'border-color .2s, box-shadow .2s' }}
                onFocus={(e) => { e.target.style.borderColor = '#0DAEFF'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
              />
              {search && (
                <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dp-text-tertiary)', display: 'flex' }}><X size={16} /></button>
              )}
            </div>
          </div>

        {/* ===== ARTIGO (leitura) ===== */}
        {selected ? (
          <motion.div
            key={`article-${selected.id}`}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
          >
            <button onClick={() => { setSearch(''); setActiveCategory(selected?.categoria || null); setSelected(null); }} style={backBtn}>
              <ArrowLeft size={16} /> {t('help.back', 'Voltar')}
            </button>
            {podeEditarFaq && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button onClick={(e) => openEdit(selected, e)} style={iconBtn} title="Editar"><Edit3 size={15} /></button>
                <button onClick={(e) => handleDelete(selected, e)} style={{ ...iconBtn, color: '#EF4444' }} title="Excluir"><Trash2 size={15} /></button>
              </div>
            )}
            <ArticleView
              article={selected}
              layout="page"
              sideToc={!isMobile}
              onNavigateHome={() => { setSelected(null); setActiveCategory(null); setSearch(''); }}
              onNavigateCategory={(cat) => { setSelected(null); setActiveCategory(cat); setSearch(''); }}
            />
          </motion.div>
        ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={screenKey}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              >
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Loader2 size={28} className="dp-spin" color="var(--dp-blue-500)" /></div>
            ) : searching ? (
              /* RESULTADOS DE BUSCA (flat) */
              articles.length === 0 ? (
                <Empty icon={Search} text={t('help.no_results', 'Nenhum artigo encontrado.')} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {articles.map((a) => <ArticleRow key={a.id} a={a} />)}
                </div>
              )
            ) : activeCategory ? (
              /* LISTA DA CATEGORIA */
              <div>
                {/* breadcrumb: Central de Ajuda › Categoria (mesmo padrão do artigo) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '12.5px', color: 'var(--dp-text-tertiary)', marginBottom: '12px' }}>
                  <button onClick={() => setActiveCategory(null)} style={crumbBtn}>{t('help.title', 'Central de Ajuda')}</button>
                  <ChevronRight size={13} />
                  <span style={{ color: 'var(--dp-text-secondary)' }}>{activeCategory}</span>
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '0 0 14px' }}>{activeCategory}</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {categoryArticles.map((a) => <ArticleRow key={a.id} a={a} />)}
                </div>
              </div>
            ) : categories.length === 0 ? (
              <Empty icon={HelpCircle} text={t('help.empty', 'Nenhum artigo publicado ainda.')} />
            ) : (
              /* HOME: CARDS DE CATEGORIA */
              <div style={{
                display: 'grid', gap: '16px',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))',
              }}>
                {categories.map(({ cat }) => { const CatIcon = categoryIcon(cat); return (
                  <button key={cat} onClick={() => setActiveCategory(cat)} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px', textAlign: 'left',
                    padding: '20px', borderRadius: '14px', cursor: 'pointer',
                    background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', transition: 'border-color 0.15s, transform 0.15s',
                  }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.3)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                  >
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(13,174,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CatIcon size={20} color="var(--dp-blue-500)" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>{cat}</div>
                    </div>
                  </button>
                ); })}
              </div>
            )}
              </motion.div>
            </AnimatePresence>
        )}
        </>
        )}
        </div>
      </main>

      {/* ===== MODAL ADMIN ===== */}
      {showModal && (
        // SEM fechar no clique fora: selecionar texto e soltar o mouse no backdrop
        // dispara click nele e PERDERIA a edição. Fecha só no X ou no Cancelar.
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ width: '100%', maxWidth: '640px', maxHeight: '92vh', overflowY: 'auto', background: 'var(--dp-bg-card)', borderRadius: '16px', border: '1px solid var(--dp-border-subtle)', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: 0 }}>{editing ? t('help.edit_article', 'Editar artigo') : t('help.new_article', 'Novo artigo')}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dp-text-tertiary)', display: 'flex' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div><label style={labelStyle}>{t('help.field_title', 'Título')} *</label>
                <input style={inputStyle} value={form.titulo} onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))} /></div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>{t('help.field_category', 'Categoria')}</label>
                  <input style={inputStyle} value={form.categoria} onChange={(e) => setForm((p) => ({ ...p, categoria: e.target.value }))} /></div>
                <div style={{ width: '110px' }}><label style={labelStyle}>{t('help.field_order', 'Ordem')}</label>
                  <input type="number" style={inputStyle} value={form.ordem} onChange={(e) => setForm((p) => ({ ...p, ordem: parseInt(e.target.value || '0', 10) }))} /></div>
              </div>
              <div><label style={labelStyle}>{t('help.field_summary', 'Resumo (subtítulo + usado na busca)')}</label>
                <input style={inputStyle} value={form.resumo} onChange={(e) => setForm((p) => ({ ...p, resumo: e.target.value }))} /></div>
              <div><label style={labelStyle}>{t('help.field_video', 'Link do vídeo (opcional)')}</label>
                <input style={inputStyle} placeholder="https://youtube.com/watch?v=..." value={form.video_url} onChange={(e) => setForm((p) => ({ ...p, video_url: e.target.value }))} /></div>
              <div><label style={labelStyle}>{t('help.field_content', 'Conteúdo do artigo')}</label>
                <MarkdownEditor value={form.conteudo_rich} onChange={(v) => setForm((p) => ({ ...p, conteudo_rich: v }))} /></div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--dp-text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.publicado} onChange={(e) => setForm((p) => ({ ...p, publicado: e.target.checked }))} />
                {t('help.field_published', 'Publicado (visível aos usuários)')}
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
              <button onClick={() => setShowModal(false)} disabled={saving} style={{ padding: '9px 16px', borderRadius: '8px', border: '1px solid var(--dp-border-subtle)', background: 'transparent', color: 'var(--dp-text-secondary)', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--dp-font)' }}>{t('help.cancel', 'Cancelar')}</button>
              <button onClick={handleSave} disabled={saving} className="dp-btn-cta dp-btn-cta--sm">
                {saving ? <Loader2 size={14} className="dp-spin" /> : null}{editing ? t('help.save', 'Salvar') : t('help.create', 'Criar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Empty = ({ icon: Icon, text }) => (
  <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
    <Icon size={40} style={{ opacity: 0.4, marginBottom: '12px' }} />
    <p>{text}</p>
  </div>
);

const backBtn = {
  display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px', background: 'none', border: 'none',
  cursor: 'pointer', padding: 0, color: 'var(--dp-blue-500)', fontSize: '13px', fontFamily: 'var(--dp-font)', fontWeight: 500,
};
const crumbBtn = {
  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
  color: 'var(--dp-text-tertiary)', fontSize: '12.5px', fontFamily: 'var(--dp-font)',
};
const iconBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '8px', border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-card)', color: 'var(--dp-text-secondary)', cursor: 'pointer' };
const iconBtnSm = { ...iconBtn, width: '28px', height: '28px', borderRadius: '6px' };
